import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Card,
  Button,
  Form,
  Table,
  Modal,
  Alert,
  Spinner,
  Badge,
  ButtonGroup,
  InputGroup,
  Row,
  Col,
} from "react-bootstrap";
import {
  FaSearch,
  FaEyeSlash,
  FaUndo,
  FaStar,
  FaTrash,
  FaEdit,
  FaExternalLinkAlt,
  FaImage,
  FaBullhorn,
} from "react-icons/fa";
import SystemAdminService from "../services/system-admin.service";
import EventBus from "../common/EventBus";

// Systemadmin: Liste "Gerade erzeugt" der Startseite.
// Ermöglicht:
// 1. Ausblenden / Wiederherstellen von Einträgen (Live-Liste & Karte)
// 2. Endgültiges Löschen aus der Datenbank (inkl. Freigabe von Test-Codes)
// 3. Vollständige Bearbeitung der Unternehmens- & Visitenkarten-Daten (inkl. Flyer-Upload)
// 4. Veröffentlichen: abgelaufene Einträge erhalten einen neuen Test-Code mit
//    frei wählbarer Laufzeit (Standard 24 h)

const DEFAULT_PUBLISH_HOURS = 24;
const MAX_PUBLISH_HOURS = 8760;

const PAGE_SIZE = 25;
const VISIBILITY_OPTIONS = [
  { id: "all", label: "Alle" },
  { id: "visible", label: "Sichtbar" },
  { id: "hidden", label: "Ausgeblendet" },
];

const fmtDateTime = (value) => {
  if (!value) return "–";
  const dt = new Date(value);
  return Number.isNaN(dt.getTime())
    ? String(value)
    : dt.toLocaleString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
};

const errorMessage = (err) =>
  (err && err.response && err.response.data && err.response.data.message) ||
  (err && err.message) ||
  "Unbekannter Fehler.";

const BoardSelfServiceLive = () => {
  const [rows, setRows] = useState([]);
  const [paging, setPaging] = useState({ page: 1, pages: 1, total: 0 });
  const [visibility, setVisibility] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(() => new Map());
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [publishHours, setPublishHours] = useState(String(DEFAULT_PUBLISH_HOURS));

  // Zustand für das Bearbeiten-Modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingPlaceId, setEditingPlaceId] = useState(null);
  const [editForm, setEditForm] = useState({
    company_name: "",
    company_address: "",
    latitude: "",
    longitude: "",
    is_promo_active: false,
    promo_title: "",
    description: "",
    website_url: "",
    contact_phone: "",
    contact_whatsapp: "",
    flyer_image_url: "",
    service_name: "",
    code_value: "",
  });
  const [editLoading, setEditLoading] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState("");
  const [flyerFile, setFlyerFile] = useState(null);
  const [flyerFilePreview, setFlyerFilePreview] = useState(null);

  const load = useCallback((page, nextSearch, nextVisibility) => {
    setLoading(true);
    setError("");
    SystemAdminService.getLiveEntries({
      page,
      pageSize: PAGE_SIZE,
      search: nextSearch || undefined,
      visibility: nextVisibility,
    })
      .then((response) => {
        const data = response.data || {};
        setRows(Array.isArray(data.entries) ? data.entries : []);
        setPaging({ page: data.page || 1, pages: data.pages || 1, total: data.total || 0 });
      })
      .catch((err) => {
        setError(errorMessage(err));
        if (err.response && err.response.status === 401) EventBus.dispatch("logout");
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load(1, search, visibility);
  }, [load, search, visibility]);

  const submitSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput.trim());
  };

  const toggleRow = (row) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(row.place_id)) next.delete(row.place_id);
      else next.set(row.place_id, row);
      return next;
    });
  };

  const allOnPageSelected = rows.length > 0 && rows.every((r) => selected.has(r.place_id));
  const togglePage = (checked) => {
    setSelected((prev) => {
      const next = new Map(prev);
      rows.forEach((r) => {
        if (checked) next.set(r.place_id, r);
        else next.delete(r.place_id);
      });
      return next;
    });
  };

  const selectedRows = Array.from(selected.values());
  const selectedToHide = selectedRows.filter((r) => !r.is_hidden);
  const selectedToRestore = selectedRows.filter((r) => r.is_hidden);
  const selectedToPublish = selectedRows.filter((r) => !r.is_active);

  const askConfirm = (action, items) => {
    if (items.length === 0) return;
    setNotice("");
    if (action === "publish") setPublishHours(String(DEFAULT_PUBLISH_HOURS));
    setConfirm({ action, items });
  };

  const publishHoursValid =
    /^\d+$/.test(publishHours) && Number(publishHours) >= 1 && Number(publishHours) <= MAX_PUBLISH_HOURS;

  const runAction = () => {
    if (!confirm) return;
    const ids = confirm.items.map((r) => r.place_id);
    let request;
    if (confirm.action === "hide") {
      request = SystemAdminService.hideLiveEntries(ids);
    } else if (confirm.action === "restore") {
      request = SystemAdminService.restoreLiveEntries(ids);
    } else if (confirm.action === "delete_permanent") {
      request = SystemAdminService.deleteLiveEntries(ids);
    } else if (confirm.action === "publish") {
      if (!publishHoursValid) return;
      request = SystemAdminService.publishLiveEntries(ids, Number(publishHours));
    } else {
      return;
    }

    setBusy(true);
    setError("");
    request
      .then((response) => {
        setNotice((response.data && response.data.message) || "Aktion erfolgreich ausgeführt.");
        setSelected((prev) => {
          const next = new Map(prev);
          ids.forEach((id) => next.delete(id));
          return next;
        });
        load(paging.page, search, visibility);
      })
      .catch((err) => {
        setError(errorMessage(err));
        if (err.response && err.response.status === 401) EventBus.dispatch("logout");
      })
      .finally(() => {
        setBusy(false);
        setConfirm(null);
      });
  };

  // --- Bearbeiten-Modal Funktionen ---
  const openEditModal = (row) => {
    setEditingPlaceId(row.place_id);
    setEditError("");
    setFlyerFile(null);
    setFlyerFilePreview(null);
    setEditModalOpen(true);
    setEditLoading(true);

    SystemAdminService.getLiveEntryDetail(row.place_id)
      .then((res) => {
        const d = res.data || {};
        setEditForm({
          company_name: d.company_name || row.company_name || "",
          company_address: d.company_address || row.company_address || "",
          latitude: d.latitude != null ? d.latitude : (row.latitude != null ? row.latitude : ""),
          longitude: d.longitude != null ? d.longitude : (row.longitude != null ? row.longitude : ""),
          is_promo_active: Boolean(d.is_promo_active),
          promo_title: d.promo_title || "",
          description: d.description || "",
          website_url: d.website_url || "",
          contact_phone: d.contact_phone || "",
          contact_whatsapp: d.contact_whatsapp || "",
          flyer_image_url: d.flyer_image_url || "",
          service_name: d.service_name || row.service_name || "",
          code_value: d.code_value || row.code_value || row.place_id,
        });
      })
      .catch((err) => {
        setEditError(errorMessage(err));
        setEditForm({
          company_name: row.company_name || "",
          company_address: row.company_address || "",
          latitude: row.latitude != null ? row.latitude : "",
          longitude: row.longitude != null ? row.longitude : "",
          is_promo_active: false,
          promo_title: "",
          description: "",
          website_url: "",
          contact_phone: "",
          contact_whatsapp: "",
          flyer_image_url: "",
          service_name: row.service_name || "",
          code_value: row.code_value || row.place_id,
        });
      })
      .finally(() => setEditLoading(false));
  };

  const saveEditModal = async (e) => {
    if (e) e.preventDefault();
    if (!editingPlaceId) return;
    setEditSaving(true);
    setEditError("");

    try {
      // 1. Text- und Statusdaten speichern
      await SystemAdminService.updateLiveEntryDetail(editingPlaceId, editForm);

      // 2. Flyer-Upload durchführen, falls neue Datei gewählt wurde
      if (flyerFile) {
        const formData = new FormData();
        formData.append("flyer", flyerFile);
        await SystemAdminService.uploadLiveEntryFlyer(editingPlaceId, formData);
      }

      setNotice(`Unternehmen „${editForm.company_name}“ erfolgreich gespeichert.`);
      setEditModalOpen(false);
      setFlyerFile(null);
      setFlyerFilePreview(null);
      load(paging.page, search, visibility);
    } catch (err) {
      setEditError(errorMessage(err));
    } finally {
      setEditSaving(false);
    }
  };

  const removeFlyer = async () => {
    if (!editingPlaceId) return;
    if (!window.confirm("Möchten Sie den Werbeflyer wirklich entfernen?")) return;
    try {
      await SystemAdminService.deleteLiveEntryFlyer(editingPlaceId);
      setEditForm((prev) => ({ ...prev, flyer_image_url: "" }));
      setFlyerFile(null);
      setFlyerFilePreview(null);
    } catch (err) {
      setEditError(errorMessage(err));
    }
  };

  return (
    <Container className="mt-4 mb-5">
      <h1 className="mb-1 h3">
        <FaStar className="me-2 text-warning" aria-hidden="true" />
        Gerade erzeugt verwalten
      </h1>
      <p className="text-muted">
        Alle Unternehmen aus der Liste „Gerade erzeugt“ der Startseite. Sie können Einträge wahlweise
        ausblenden, vollständig bearbeiten (inkl. Werbeplatz und Flyer) oder endgültig aus der Datenbank löschen.
      </p>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert variant="success" dismissible onClose={() => setNotice("")}>
          {notice}
        </Alert>
      )}

      <Card className="mb-3">
        <Card.Body className="d-flex flex-column flex-lg-row gap-2 align-items-lg-center">
          <Form onSubmit={submitSearch} className="flex-grow-1">
            <InputGroup>
              <Form.Control
                type="search"
                placeholder="Unternehmen, Adresse oder Dienst suchen"
                aria-label="Unternehmen, Adresse oder Dienst suchen"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
              <Button type="submit" variant="outline-secondary" aria-label="Suchen">
                <FaSearch aria-hidden="true" />
              </Button>
            </InputGroup>
          </Form>
          <ButtonGroup aria-label="Filter nach Sichtbarkeit" className="flex-wrap">
            {VISIBILITY_OPTIONS.map((option) => (
              <Button
                key={option.id}
                variant={visibility === option.id ? "primary" : "outline-primary"}
                onClick={() => setVisibility(option.id)}
                aria-pressed={visibility === option.id}
              >
                {option.label}
              </Button>
            ))}
          </ButtonGroup>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header className="d-flex flex-wrap gap-2 align-items-center">
          <span className="small text-muted me-auto">
            {paging.total} Einträge{search ? ` (gefiltert: „${search}“)` : ""}
            {selected.size > 0 && (
              <>
                {" · "}
                <strong>{selected.size} markiert</strong>{" "}
                <Button variant="link" size="sm" className="p-0 align-baseline" onClick={() => setSelected(new Map())}>
                  Auswahl aufheben
                </Button>
              </>
            )}
          </span>
          <Button
            variant="outline-warning"
            size="sm"
            disabled={busy || selectedToHide.length === 0}
            onClick={() => askConfirm("hide", selectedToHide)}
          >
            <FaEyeSlash className="me-1" aria-hidden="true" />
            Markierte ausblenden ({selectedToHide.length})
          </Button>
          <Button
            variant="outline-success"
            size="sm"
            disabled={busy || selectedToRestore.length === 0}
            onClick={() => askConfirm("restore", selectedToRestore)}
          >
            <FaUndo className="me-1" aria-hidden="true" />
            Markierte wiederherstellen ({selectedToRestore.length})
          </Button>
          <Button
            variant="outline-info"
            size="sm"
            disabled={busy || selectedToPublish.length === 0}
            onClick={() => askConfirm("publish", selectedToPublish)}
            title="Abgelaufenen Einträgen einen neuen Test-Code geben"
          >
            <FaBullhorn className="me-1" aria-hidden="true" />
            Markierte veröffentlichen ({selectedToPublish.length})
          </Button>
          <Button
            variant="danger"
            size="sm"
            disabled={busy || selectedRows.length === 0}
            onClick={() => askConfirm("delete_permanent", selectedRows)}
          >
            <FaTrash className="me-1" aria-hidden="true" />
            Markierte endgültig löschen ({selectedRows.length})
          </Button>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" /> Einträge werden geladen …
            </div>
          ) : rows.length === 0 ? (
            <div className="text-muted text-center py-4">Keine Einträge gefunden.</div>
          ) : (
            <Table responsive hover className="mb-0 align-middle">
              <thead>
                <tr>
                  <th style={{ width: 44 }}>
                    <Form.Check
                      type="checkbox"
                      id="live-select-page"
                      aria-label="Alle Einträge dieser Seite markieren"
                      checked={allOnPageSelected}
                      onChange={(e) => togglePage(e.target.checked)}
                    />
                  </th>
                  <th>Unternehmen</th>
                  <th>Dienst</th>
                  <th>Erzeugt</th>
                  <th>Status</th>
                  <th className="text-end">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.place_id} className={row.is_hidden ? "table-secondary" : undefined}>
                    <td>
                      <Form.Check
                        type="checkbox"
                        id={`live-row-${row.place_id}`}
                        aria-label={`${row.company_name} markieren`}
                        checked={selected.has(row.place_id)}
                        onChange={() => toggleRow(row)}
                      />
                    </td>
                    <td style={{ minWidth: 220 }}>
                      <strong className="d-block">{row.company_name}</strong>
                      <span className="small text-muted">{row.company_address}</span>
                    </td>
                    <td className="small">{row.service_name || "–"}</td>
                    <td className="small text-nowrap">{fmtDateTime(row.created_at)}</td>
                    <td>
                      <div className="d-flex flex-wrap gap-1">
                        {row.is_hidden ? (
                          <Badge bg="secondary">Ausgeblendet</Badge>
                        ) : (
                          <Badge bg="success">Sichtbar</Badge>
                        )}
                        {row.is_active ? (
                          <Badge bg="primary">Code aktiv</Badge>
                        ) : (
                          <Badge bg="light" text="dark">Test abgelaufen</Badge>
                        )}
                      </div>
                    </td>
                    <td className="text-end text-nowrap">
                      {/* Nur Icons; die Beschriftung erscheint als Tooltip (title) und für Screenreader (aria-label). */}
                      <div className="d-inline-flex gap-1">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          disabled={busy}
                          onClick={() => openEditModal(row)}
                          title="Bearbeiten: Unternehmen & Visitenkarte"
                          aria-label={`${row.company_name} bearbeiten`}
                        >
                          <FaEdit aria-hidden="true" />
                        </Button>
                        <Button
                          variant="outline-info"
                          size="sm"
                          disabled={busy || row.is_active}
                          onClick={() => askConfirm("publish", [row])}
                          title={row.is_active ? "Veröffentlichen: Code ist bereits aktiv" : "Veröffentlichen: neuen Test-Code vergeben"}
                          aria-label={`${row.company_name} veröffentlichen`}
                        >
                          <FaBullhorn aria-hidden="true" />
                        </Button>
                        {row.is_hidden ? (
                          <Button
                            variant="outline-success"
                            size="sm"
                            disabled={busy}
                            onClick={() => askConfirm("restore", [row])}
                            title="Wiederherstellen: wieder auf Startseite & Karte anzeigen"
                            aria-label={`${row.company_name} wiederherstellen`}
                          >
                            <FaUndo aria-hidden="true" />
                          </Button>
                        ) : (
                          <Button
                            variant="outline-warning"
                            size="sm"
                            disabled={busy}
                            onClick={() => askConfirm("hide", [row])}
                            title="Ausblenden: aus „Gerade erzeugt“ und von der Karte"
                            aria-label={`${row.company_name} ausblenden`}
                          >
                            <FaEyeSlash aria-hidden="true" />
                          </Button>
                        )}
                        <Button
                          variant="outline-danger"
                          size="sm"
                          disabled={busy}
                          onClick={() => askConfirm("delete_permanent", [row])}
                          title="Löschen: endgültig aus der Datenbank"
                          aria-label={`${row.company_name} endgültig löschen`}
                        >
                          <FaTrash aria-hidden="true" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
        {paging.pages > 1 && (
          <Card.Footer className="d-flex justify-content-between align-items-center">
            <Button
              size="sm"
              variant="outline-secondary"
              disabled={loading || paging.page <= 1}
              onClick={() => load(paging.page - 1, search, visibility)}
            >
              Zurück
            </Button>
            <span className="small">
              Seite {paging.page} / {paging.pages}
            </span>
            <Button
              size="sm"
              variant="outline-secondary"
              disabled={loading || paging.page >= paging.pages}
              onClick={() => load(paging.page + 1, search, visibility)}
            >
              Weiter
            </Button>
          </Card.Footer>
        )}
      </Card>

      {/* BESTÄTIGUNGS-MODAL (Ausblenden / Wiederherstellen / Endgültig Löschen) */}
      <Modal show={!!confirm} onHide={() => !busy && setConfirm(null)} centered>
        <Modal.Header closeButton={!busy}>
          <Modal.Title>
            {confirm && confirm.action === "hide"
              ? "Einträge ausblenden"
              : confirm && confirm.action === "restore"
              ? "Einträge wiederherstellen"
              : confirm && confirm.action === "publish"
              ? "Einträge veröffentlichen"
              : "⚠️ Einträge endgültig löschen"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {confirm && (
            <>
              {confirm.action === "delete_permanent" ? (
                <Alert variant="danger">
                  <strong>Achtung: Unwiderruflich!</strong>
                  <p className="mb-0 mt-1">
                    {confirm.items.length === 1
                      ? `Der Eintrag „${confirm.items[0].company_name}“ wird vollständig aus der Datenbank gelöscht. Alle Test-Einträge, Ausblendungen und Stammdaten werden unwiderruflich entfernt. Eventuelle Test-Codes werden wieder freigegeben.`
                      : `${confirm.items.length} Einträge werden vollständig aus der Datenbank gelöscht. Alle Test-Einträge, Ausblendungen und Stammdaten werden unwiderruflich entfernt. Eventuelle Test-Codes werden wieder freigegeben.`}
                  </p>
                </Alert>
              ) : confirm.action === "publish" ? (
                <>
                  <p>
                    {confirm.items.length} Eintrag/Einträge erhalten einen neuen Test-Code. Scans leiten für die
                    gewählte Laufzeit wieder weiter; ausgeblendete Einträge werden dabei wieder sichtbar.
                  </p>
                  <Form.Group className="mb-3" controlId="publish-hours">
                    <Form.Label>Laufzeit in Stunden</Form.Label>
                    <Form.Control
                      type="number"
                      min={1}
                      max={MAX_PUBLISH_HOURS}
                      step={1}
                      value={publishHours}
                      isInvalid={!publishHoursValid}
                      onChange={(e) => setPublishHours(e.target.value)}
                    />
                    <Form.Text className="text-muted">
                      Standard {DEFAULT_PUBLISH_HOURS} h (z. B. 168 = 1 Woche, 720 = 30 Tage).
                    </Form.Text>
                    <Form.Control.Feedback type="invalid">
                      Bitte eine ganze Zahl von 1 bis {MAX_PUBLISH_HOURS} angeben.
                    </Form.Control.Feedback>
                  </Form.Group>
                </>
              ) : (
                <p>
                  {confirm.action === "hide"
                    ? `${confirm.items.length} Eintrag/Einträge werden aus „Gerade erzeugt“ und von der Live-Karte ausgeblendet. QR-Codes und Buchungen bleiben erhalten.`
                    : `${confirm.items.length} Eintrag/Einträge werden wieder in „Gerade erzeugt“ und auf der Live-Karte angezeigt.`}
                </p>
              )}
              <ul className="small mb-0" style={{ maxHeight: 200, overflowY: "auto" }}>
                {confirm.items.slice(0, 50).map((r) => (
                  <li key={r.place_id}>
                    <strong>{r.company_name}</strong> {r.company_address && `(${r.company_address})`}
                  </li>
                ))}
                {confirm.items.length > 50 && <li>… und {confirm.items.length - 50} weitere</li>}
              </ul>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" disabled={busy} onClick={() => setConfirm(null)}>
            Abbrechen
          </Button>
          <Button
            variant={
              confirm && confirm.action === "delete_permanent"
                ? "danger"
                : confirm && confirm.action === "hide"
                ? "warning"
                : confirm && confirm.action === "publish"
                ? "info"
                : "success"
            }
            disabled={busy || (confirm && confirm.action === "publish" && !publishHoursValid)}
            onClick={runAction}
          >
            {busy ? (
              <Spinner animation="border" size="sm" />
            ) : confirm && confirm.action === "delete_permanent" ? (
              <>
                <FaTrash className="me-1" /> Endgültig löschen
              </>
            ) : confirm && confirm.action === "hide" ? (
              "Ausblenden"
            ) : confirm && confirm.action === "publish" ? (
              <>
                <FaBullhorn className="me-1" /> Veröffentlichen
              </>
            ) : (
              "Wiederherstellen"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* BEARBEITEN-MODAL FÜR UNTERNEHMEN & VISITENKARTE */}
      <Modal show={editModalOpen} onHide={() => !editSaving && setEditModalOpen(false)} size="lg" centered scrollable>
        <Modal.Header closeButton={!editSaving}>
          <Modal.Title className="h5">
            <FaEdit className="me-2 text-primary" />
            Unternehmen &amp; Visitenkarte bearbeiten
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editError && (
            <Alert variant="danger" dismissible onClose={() => setEditError("")}>
              {editError}
            </Alert>
          )}
          {editLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" /> Daten werden geladen …
            </div>
          ) : (
            <Form onSubmit={saveEditModal}>
              <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 p-2 bg-light rounded border gap-2">
                <div>
                  <small className="text-muted d-block">Place-ID / Referenz:</small>
                  <code className="text-dark small text-break">{editingPlaceId}</code>
                </div>
                <Button
                  variant="outline-primary"
                  size="sm"
                  href={`/v/${editForm.code_value || editingPlaceId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Visitenkarte in neuem Tab öffnen"
                >
                  <FaExternalLinkAlt className="me-1" /> Visitenkarte live ansehen
                </Button>
              </div>

              {/* 1. Basisdaten */}
              <h6 className="fw-bold text-primary border-bottom pb-1 mb-3">1. Basis- &amp; Standortdaten</h6>
              <Row className="g-3 mb-3">
                <Col md={12}>
                  <Form.Group controlId="editCompanyName">
                    <Form.Label className="small fw-semibold">Firmenname / Unternehmensbezeichnung *</Form.Label>
                    <Form.Control
                      type="text"
                      required
                      value={editForm.company_name}
                      onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })}
                      placeholder="z. B. Vintage Gold Köln Schmuck..."
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group controlId="editCompanyAddress">
                    <Form.Label className="small fw-semibold">Vollständige Adresse *</Form.Label>
                    <Form.Control
                      type="text"
                      required
                      value={editForm.company_address}
                      onChange={(e) => setEditForm({ ...editForm, company_address: e.target.value })}
                      placeholder="z. B. Frankfurter Str. 71, 51065 Köln, Germany"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="editLatitude">
                    <Form.Label className="small fw-semibold">Breitengrad (Latitude)</Form.Label>
                    <Form.Control
                      type="number"
                      step="any"
                      value={editForm.latitude}
                      onChange={(e) => setEditForm({ ...editForm, latitude: e.target.value })}
                      placeholder="z. B. 50.9632"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="editLongitude">
                    <Form.Label className="small fw-semibold">Längengrad (Longitude)</Form.Label>
                    <Form.Control
                      type="number"
                      step="any"
                      value={editForm.longitude}
                      onChange={(e) => setEditForm({ ...editForm, longitude: e.target.value })}
                      placeholder="z. B. 7.0094"
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* 2. Visitenkarten- & Werbedaten */}
              <h6 className="fw-bold text-primary border-bottom pb-1 mb-3">2. Visitenkarte &amp; Werbeplatz</h6>

              <Form.Group controlId="editPromoActive" className="mb-3 p-2 bg-light rounded border">
                <Form.Check
                  type="switch"
                  id="promo-active-switch"
                  label={<strong>Werbeplatz aktiv (Visitenkarte, Werbeflyer &amp; Kontaktdaten öffentlich hervorheben)</strong>}
                  checked={editForm.is_promo_active}
                  onChange={(e) => setEditForm({ ...editForm, is_promo_active: e.target.checked })}
                />
              </Form.Group>

              <Row className="g-3 mb-3">
                <Col md={12}>
                  <Form.Group controlId="editPromoTitle">
                    <Form.Label className="small fw-semibold">Werbetitel / Slogan</Form.Label>
                    <Form.Control
                      type="text"
                      value={editForm.promo_title}
                      onChange={(e) => setEditForm({ ...editForm, promo_title: e.target.value })}
                      placeholder="z. B. Ihr vertrauensvoller Partner für Goldankauf und Schmuck in Köln"
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group controlId="editDescription">
                    <Form.Label className="small fw-semibold">Beschreibungstext</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={editForm.description}
                      onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                      placeholder="Ausführliche Unternehmensbeschreibung, Angebote, Öffnungszeiten etc."
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId="editWebsite">
                    <Form.Label className="small fw-semibold">Website-URL</Form.Label>
                    <Form.Control
                      type="url"
                      value={editForm.website_url}
                      onChange={(e) => setEditForm({ ...editForm, website_url: e.target.value })}
                      placeholder="https://beispiel.de"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId="editPhone">
                    <Form.Label className="small fw-semibold">Telefonnummer</Form.Label>
                    <Form.Control
                      type="text"
                      value={editForm.contact_phone}
                      onChange={(e) => setEditForm({ ...editForm, contact_phone: e.target.value })}
                      placeholder="+49 221 1234567"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId="editWhatsapp">
                    <Form.Label className="small fw-semibold">WhatsApp-Nummer</Form.Label>
                    <Form.Control
                      type="text"
                      value={editForm.contact_whatsapp}
                      onChange={(e) => setEditForm({ ...editForm, contact_whatsapp: e.target.value })}
                      placeholder="+49 170 1234567"
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* 3. Flyer / Banner */}
              <h6 className="fw-bold text-primary border-bottom pb-1 mb-3">3. Werbeflyer &amp; Bannerbild</h6>
              <div className="mb-3">
                {editForm.flyer_image_url && !flyerFilePreview ? (
                  <div className="d-flex flex-wrap align-items-center gap-3 p-3 bg-light rounded border mb-2">
                    <img
                      src={editForm.flyer_image_url}
                      alt="Aktueller Flyer"
                      style={{ width: 100, height: 100, objectFit: "cover", borderRadius: 8 }}
                      className="border shadow-sm"
                    />
                    <div className="flex-grow-1">
                      <span className="small text-muted d-block mb-1">Aktuell hinterlegter Werbeflyer:</span>
                      <code className="small d-block text-break mb-2">{editForm.flyer_image_url}</code>
                      <Button variant="outline-danger" size="sm" onClick={removeFlyer}>
                        <FaTrash className="me-1" /> Flyer entfernen
                      </Button>
                    </div>
                  </div>
                ) : flyerFilePreview ? (
                  <div className="d-flex flex-wrap align-items-center gap-3 p-3 bg-light rounded border mb-2">
                    <img
                      src={flyerFilePreview}
                      alt="Neuer Flyer Vorschau"
                      style={{ width: 100, height: 100, objectFit: "cover", borderRadius: 8 }}
                      className="border shadow-sm"
                    />
                    <div className="flex-grow-1">
                      <span className="small text-success fw-bold d-block mb-1">Neues Bild zur Aktualisierung ausgewählt:</span>
                      <span className="small d-block text-muted mb-2">{flyerFile && flyerFile.name}</span>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => {
                          setFlyerFile(null);
                          setFlyerFilePreview(null);
                        }}
                      >
                        Auswahl aufheben
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="text-muted small mb-2">
                    Noch kein Flyer hinterlegt. Es wird der Standard-Farbverlauf mit den Initialen angezeigt.
                  </p>
                )}

                <Form.Group controlId="editFlyerUpload">
                  <Form.Label className="small fw-semibold">
                    <FaImage className="me-1" />{" "}
                    {editForm.flyer_image_url ? "Neuen Flyer hochladen (ersetzt den bisherigen)" : "Werbeflyer hochladen"}
                  </Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={(e) => {
                      const file = e.target.files && e.target.files[0];
                      if (file) {
                        setFlyerFile(file);
                        setFlyerFilePreview(URL.createObjectURL(file));
                      }
                    }}
                  />
                  <Form.Text className="text-muted small">
                    Erlaubte Formate: JPEG, PNG, WebP, GIF. Maximale Dateigröße: 5 MB.
                  </Form.Text>
                </Form.Group>
              </div>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" disabled={editSaving} onClick={() => setEditModalOpen(false)}>
            Abbrechen
          </Button>
          <Button variant="primary" disabled={editSaving || editLoading} onClick={saveEditModal}>
            {editSaving ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" /> Speichern …
              </>
            ) : (
              "Änderungen speichern"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default BoardSelfServiceLive;
